import { ColorItem } from "components/ColorItem/ColorItem"
import { UICustomizationData } from "pages/UICustomization/types.ts"
import { ChangeEvent } from "react"
import { useTranslation } from "react-i18next"
import { isDarkMode } from "utils/isDarkMode"
import { Box, Flex, TextInput, Typography } from "@strapi/design-system"
import { defaultStyles } from "./defaultStyles.ts"
import { getStylesTemplate } from "./stylesTemplate"

interface UICustomizationStylesProps {
  data: UICustomizationData
  onChange: (data: UICustomizationData) => void
}

const ButtonExample = ({ stylesData }) => {
  const { t } = useTranslation()
  const buttonTypes = ["primary", "secondary"]
  const buttonStates = ["normal", "hover", "active", "disabled"]

  return (
    <Flex background="#fff" gap={4} direction="column" padding={4} borderRadius="4px">
      {buttonTypes.map((buttonType) => (
        <Flex background="#fff" gap={4} key={buttonType}>
          {buttonStates.map((buttonState) => (
            <button
              key={`${buttonType}-${buttonState}`}
              style={{
                padding: "8px 24px",
                fontSize: "14px",
                lineHeight: "20px",
                borderRadius: stylesData["tint-button-base-radius"],
                background:
                  stylesData[
                    `tint-button-${buttonType}-background${
                      buttonState === "normal" ? "" : `-${buttonState}`
                    }`
                  ],
                color:
                  stylesData[
                    `tint-button-${buttonType}-text${
                      buttonState === "normal" ? "" : `-${buttonState}`
                    }`
                  ],
                borderColor:
                  stylesData[
                    `tint-button-${buttonType}-border${
                      buttonState === "normal" ? "" : `-${buttonState}`
                    }`
                  ],
                borderWidth:
                  stylesData[
                    `tint-button-${buttonType}-border-width${
                      buttonState === "normal" ? "" : `-${buttonState}`
                    }`
                  ],
                borderStyle: "solid",
                outline: "2px solid transparent",
                outlineOffset: "2px",
              }}
            >
              {t(`uiCustomizationStyles.buttonType.${buttonType}`)}&nbsp;
              {t(`uiCustomizationStyles.buttonState.${buttonState}`)}
            </button>
          ))}
        </Flex>
      ))}
    </Flex>
  )
}

export const UICustomizationStyles = ({ data, onChange }: UICustomizationStylesProps) => {
  const { t } = useTranslation()
  const stylesTemplate = getStylesTemplate(t)
  const isDark = isDarkMode()
  const stylesData = {
    ...defaultStyles,
    ...data.styles,
  }

  return (
    <div className="flex gap-4 flex-col">
      {stylesTemplate.map(({ title, styles }) => (
        <Box padding={6} background="neutral0" hasRadius key={title}>
          <Typography variant="beta">{title}</Typography>
          <Flex style={{ flexFlow: "row wrap" }} gap={4}>
            {styles.map(({ title, key, type }, index) => (
              <Box
                key={type !== "separator" ? key : `separator-${index}-${title}`}
                style={{
                  width: type === "separator" || type === "buttonExample" ? "100%" : "300px",
                }}
              >
                {type === "buttonExample" && <ButtonExample stylesData={stylesData} />}
                {type !== "separator" && (
                  <>
                    <Typography variant="epsilon">{title}</Typography>
                    <Box padding={1} />
                  </>
                )}
                {type === "separator" && (
                  <hr style={{ borderColor: isDark ? "#4a4a6a" : "#dcdce4" }} />
                )}
                {type === "color" && (
                  <ColorItem
                    key={key}
                    value={stylesData[key]}
                    onChange={(value) =>
                      onChange({ ...data, styles: { ...stylesData, [key]: value } })
                    }
                  />
                )}
                {type === "size" && (
                  <Flex>
                    <TextInput
                      error={!stylesData[key] || !stylesData[key].length}
                      aria-label={title}
                      name={key}
                      value={stylesData[key]}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChange({ ...data, styles: { ...stylesData, [key]: e.target.value } })
                      }
                    />
                  </Flex>
                )}
                {type !== "separator" && (
                  <Typography variant="omega" textColor="neutral300">
                    {key}
                  </Typography>
                )}
              </Box>
            ))}
          </Flex>
        </Box>
      ))}
    </div>
  )
}
